import React, { useState } from 'react';
import PreloadGeneratorView from './preload-generator-view';
import { parseStyles } from './lib/parseStyles';
import { extractPreloads, formatPreloadTags } from './lib/extractPreloads';
import ZApiClient from '../../utils/client/z-api-client';

const PreloadGeneratorContainer = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [outputText, setOutputText] = useState('');
  const [copied, setCopied] = useState(false);
  const [blockOverride, setBlockOverride] = useState('');

  const handleGenerate = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) { setError('Please enter a URL.'); return; }
    try { new URL(trimmedUrl); } catch { setError('Please enter a valid URL.'); return; }

    setLoading(true);
    setError(null);
    setResult(null);
    setOutputText('');
    setCopied(false);

    try {
      const client = ZApiClient.getClient();
      const response = await client.post('/v2/web_proxies', { url: trimmedUrl });

      if (response.error) {
        setError(response.error);
        return;
      }

      const html = response.html;
      if (!html) {
        setError('Received empty response from the server.');
        return;
      }

      const cssTexts = parseStyles(html);
      if (cssTexts.length === 0) {
        setError('No inline styles found on this page. It may not be an Unbounce landing page.');
        return;
      }

      const overrideId = blockOverride.trim()
        ? (blockOverride.trim().startsWith('#') ? blockOverride.trim() : `#${blockOverride.trim()}`)
        : null;

      const { error: extractError, preloads, heroBlockId } = extractPreloads(cssTexts, overrideId);

      if (extractError || preloads.length === 0) {
        setError('No hero background images found. The page may use a different structure.');
        return;
      }

      const output = formatPreloadTags(preloads);
      const categories = preloads.map((p) => p.category);

      setResult({ preloads, heroBlockId, categories });
      setOutputText(output);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!outputText) return;
    try {
      await navigator.clipboard.writeText(outputText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard.');
    }
  };

  return (
    <PreloadGeneratorView
      url={url}
      onUrlChange={setUrl}
      blockOverride={blockOverride}
      onBlockOverrideChange={setBlockOverride}
      loading={loading}
      error={error}
      result={result}
      outputText={outputText}
      onOutputTextChange={setOutputText}
      copied={copied}
      onGenerate={handleGenerate}
      onCopy={handleCopy}
    />
  );
};

export default PreloadGeneratorContainer;
